import React from "react";
import { MessageCircle, Phone, Clock } from "lucide-react";
import { COMPANY } from "../content";

/**
 * Slim conversion-focused CTA band.
 *  - "Typical initial response · 30 minutes" estimate (with clock icon).
 *  - WhatsApp CTA: "Share Your Window Details".
 *  - Tel fallback.
 *  - Minimal, premium — matches the burgundy/gold/warm-white palette.
 *
 * `prefilled` is the plaintext message; if `useUnifiedLink` is true, the
 * sitewide unified WhatsApp URL is used instead.
 */
export default function QuickQuoteStrip({
  testid = "quick-quote-strip",
  prefilled = "Hi Time Furniture, here are my window details for a quotation.",
  variant = "burgundy",
  useUnifiedLink = false,
}) {
  const waHref = useUnifiedLink
    ? `https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappCurtainsText}`
    : `https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(prefilled)}`;

  const isLight = variant === "light";
  const wrapBg = isLight ? "bg-[#FAF5EC]" : "bg-[#6E1F2C] text-[#FAF5EC]";
  const goldText = isLight ? "text-[#6E1F2C]" : "text-[#D4A963]";
  const headingColor = isLight ? "text-[#1C110C]" : "text-[#FAF5EC]";
  const subColor = isLight ? "text-[#3E2A22]" : "text-[#EDE1C8]";
  const primaryBtn = isLight
    ? "bg-[#6E1F2C] text-white hover:bg-[#56151F]"
    : "bg-[#D4A963] text-[#1C110C] hover:bg-[#c9a166]";
  const ghostBtn = isLight
    ? "border border-[#6E1F2C]/40 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-white"
    : "border border-[#D4A963]/60 text-[#FAF5EC] hover:bg-[#D4A963] hover:text-[#1C110C]";

  return (
    <section
      data-testid={testid}
      className={`${wrapBg} ${isLight ? "border-y border-[#6E1F2C]/15" : ""}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left — promise */}
          <div className="lg:col-span-7">
            <div
              className={`inline-flex items-center gap-2.5 ${goldText} text-[10px] md:text-[11px] tracking-[0.32em] uppercase mb-4`}
            >
              <Clock size={14} strokeWidth={2.2} /> Typical First Reply · 30 Minutes
            </div>
            <p
              className={`font-serif-display text-2xl md:text-3xl lg:text-[34px] leading-[1.2] tracking-tight ${headingColor}`}
            >
              Send your window size — typical first reply within 30 minutes.
            </p>
            <p className={`mt-3 text-sm md:text-base ${subColor} max-w-2xl`}>
              Snap a photo of your space and share approximate measurements.
              Typical initial response during working hours — a detailed
              quotation is usually prepared within 24 hours once measurements
              and specifications are confirmed.
            </p>
          </div>

          {/* Right — actions */}
          <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 lg:items-stretch">
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testid}-whatsapp`}
              className={`inline-flex items-center justify-center gap-3 ${primaryBtn} px-7 py-5 text-[11px] tracking-[0.28em] uppercase font-medium transition-all`}
            >
              <MessageCircle size={15} strokeWidth={2.2} />
              Share Your Window Details
            </a>
            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              data-testid={`${testid}-call`}
              className={`inline-flex items-center justify-center gap-3 ${ghostBtn} px-7 py-5 text-[11px] tracking-[0.28em] uppercase font-medium transition-all`}
            >
              <Phone size={14} strokeWidth={2.2} /> Call Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
